import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {WelfareProject} from '../class/welfare-project';
import {Observable} from 'rxjs';
import {LifeProject} from '../class/lifeproject/life-project';
import {BadEvent} from '../class/bad-event';
import {WelfareProjectResult} from '../class/result/welfare-project-result';
import {Configuration} from '../class/config/configuration';
import {PersonRetirement} from '../class/lifeproject/retirement/person-retirement';
import {WelfareProjectResultArchive} from '../class/welfare-project-result-archive';
import {FileReference} from '../class/file-reference';
import {environment} from '../../../environments/environment';
import {WelfyBusinessProject} from '../class/business/welfy-business-project';
import {WelfyBusinessResult} from '../class/business/welfy-business-result';
import {KeyValueStruct} from '../class/lifeproject/retirement/epheso/key-value-struct';
import {EphesoAnalisys} from '../class/lifeproject/retirement/epheso/epheso-analisys';
import {RisultatiPrevidenza} from '../class/lifeproject/retirement/epheso/risultati-previdenza';
import {WelfareLightProject} from '../class/welfare-light-project';
import {WelfareLightResult} from '../class/result/welfare-light-result';
import {PrivacyOption} from '../class/privacy-option';
import {UPLOAD_KIND} from '../class/file/upload-kind.enum';
import {DenProject} from '../class/backoffice/idd/den-project';
import {AniaRetirementResponse} from '../class/lifeproject/retirement/ania/response/retirement/ania-retirement-response';
import {RetirementJobCategory} from '../class/lifeproject/retirement/retirement-job-category';
import {BoardProject} from '../class/lifeproject/board-project';
import {Collaborator} from '@feature-module/user/model/collaborator';

export enum RequestMethod {
  GET,
  POST,
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(private http: HttpClient) {

  }

  get url() {
    return environment.backendUrl;
  }

  get RequestMethod() {
    return RequestMethod;
  }

  get authToken() {
    return localStorage.getItem('token');
  }

  saveProject(project: any, appName: string): Observable<any> {
    return this.sendRequest<any>('/api/project/' + appName + '/save', RequestMethod.POST, project);
  }

  deleteProject(project: any, appName: string): Observable<any> {
    return this.sendRequest<any>('/api/project/' + appName + '/delete', RequestMethod.POST, project);
  }

  searchProject(searchString: string, appName: string): Observable<any[]> {
    return this.sendRequest<any>('/api/project/' + appName + '/search?value=' + searchString, RequestMethod.GET);
  }

  getProjectResult(project: WelfareProject): Observable<WelfareProjectResult> {
    return this.sendRequest<WelfareProjectResult>('/api/project/welfy/result', RequestMethod.POST, project);
  }

  getLightProjectResult(project: WelfareLightProject): Observable<WelfareLightResult> {
    return this.sendRequest<WelfareLightResult>('/api/project/light/result', RequestMethod.POST, project);
  }

  loadSavedProjects(appName: string): Observable<any[]> {
    return this.sendRequest<any[]>('/simulation/projects', RequestMethod.GET);
  }

  loadProjectByID(id: string, appName: string): Observable<any> {
    return this.sendRequest<any>('/projects/load/' + id, RequestMethod.GET);
  }

  loadLifeProjects(): Observable<LifeProject[]> {
    return this.sendRequest<LifeProject[]>('/api/lifeprojects/', RequestMethod.GET);
  }

  loadBadEvents(): Observable<BadEvent[]> {
    return this.sendRequest<BadEvent[]>('/api/badevents/', RequestMethod.GET);
  }

  loadProjectArchive(projectID: string): Observable<WelfareProjectResultArchive[]> {
    return this.sendRequest<WelfareProjectResultArchive[]>('/api/project/archive/' + projectID, RequestMethod.GET);
  }

  loadCCNL(): Observable<FileReference[]> {
    return this.sendRequest<FileReference[]>('/api/file/ccnl/list', RequestMethod.GET);
  }

  loadCompanyFolderFiles(path: string): Observable<FileReference[]> {
    return this.sendRequest<FileReference[]>('/api/file/company/' + path + '/list', RequestMethod.GET);
  }

  getFrontedConfiguration(): Observable<Configuration> {
    return this.sendRequest<Configuration>('/api/config/', RequestMethod.GET);
  }

  getRetirementValue(personRetirement: PersonRetirement): Observable<PersonRetirement> {
    return this.sendRequest<PersonRetirement>('/api/lifeprojects/retirement', RequestMethod.POST, personRetirement);
  }

  businessShowResult(businessProject: WelfyBusinessProject): Observable<WelfyBusinessResult> {
    return this.sendRequest<WelfyBusinessResult>('/api/business/result', RequestMethod.POST, businessProject);
  }

  saveBusinessProject(businessProject: WelfyBusinessProject): Observable<WelfyBusinessProject> {
    return this.sendRequest<WelfyBusinessProject>('/api/business/save', RequestMethod.POST, businessProject);
  }

  loadBusinessProjects(): Observable<WelfyBusinessProject[]> {
    return this.sendRequest<WelfyBusinessProject[]>('/api/business/load', RequestMethod.GET);
  }

  loadBusinessProjectById(projectID): Observable<WelfyBusinessProject> {
    return this.sendRequest<WelfyBusinessProject>('/api/business/load/' + projectID, RequestMethod.GET);
  }

  deleteBusinessProject(welfyBusinessProject: WelfyBusinessProject) {
    return this.sendRequest('/api/business/delete', RequestMethod.POST, welfyBusinessProject);
  }

  getEphesoCategory(): Observable<KeyValueStruct[]> {
    return this.sendRequest<KeyValueStruct[]>('/api/lifeprojects/epheso/category', RequestMethod.GET);
  }

  getEphesoCareers(): Observable<KeyValueStruct[]> {
    return this.sendRequest<KeyValueStruct[]>('/api/lifeprojects/epheso/careers', RequestMethod.GET);
  }

  getEphesoResult(analisys: EphesoAnalisys): Observable<RisultatiPrevidenza> {
    return this.sendRequest<RisultatiPrevidenza>('/api/lifeprojects/epheso/results', RequestMethod.POST, analisys);
  }


  uploadFiles(data: FormData, objectID: string, uploadKind: UPLOAD_KIND): Observable<FileReference[]> {
    return this.sendRequest<FileReference[]>('/api/file/' + objectID + '/' + uploadKind + '/upload/files/', RequestMethod.POST, data, true, true);
  }

  getFileReferences(objectID: string, kind: UPLOAD_KIND): Observable<FileReference[]> {
    return this.sendRequest<FileReference[]>('/api/file/' + objectID + '/files', RequestMethod.GET);
  }


  public sendRequest<T>(endPoint: string, method: RequestMethod, body?: any, useUploadHeader?: boolean, ignoreJson?: boolean): Observable<T> {
    // build endpoint url
    endPoint = this.url + endPoint;
    const data = ignoreJson ? body : JSON.stringify(body);

    switch (method) {
      case RequestMethod.GET: {
        return this.http.get<T>(endPoint, {headers: useUploadHeader ? this.buildUploadHeader() : this.buildRequestHeader()});
      }
      case RequestMethod.POST: {
        return this.http.post<T>(endPoint, data, {headers: useUploadHeader ? this.buildUploadHeader() : this.buildRequestHeader()});
      }
    }
  }

  public getIddQuestions(table: string, category?: string): Observable<any> {
    const cat = category ? '&category=' + category : '';
    const url = 'https://x8nkw0tuf1.execute-api.eu-west-1.amazonaws.com/production/welfy_db_query?table=' + table + cat;
    return this.http.get<any>(url);
  }

  public getIddCategories(): Observable<any> {
    return this.http.get<any>('https://x8nkw0tuf1.execute-api.eu-west-1.amazonaws.com/production/welfy_db_query?table=den_questions_category');
  }

  public getIddQuestionsByCategory(category: string): Observable<any> {
    return this.http.get<any>('https://x8nkw0tuf1.execute-api.eu-west-1.amazonaws.com/production/welfy_db_query?table=den_questions&category=' + category);
  }

  public createDenProject(customerID: string): Observable<DenProject> {
    return this.sendRequest<DenProject>('/api/idd/' + customerID + '/create', RequestMethod.GET);
  }

  public createDenProjectCopy(projectID: string): Observable<DenProject> {
    return this.sendRequest<DenProject>('/api/idd/copy/' + projectID, RequestMethod.GET);
  }

  deleteFileReference(fileReference: FileReference): Observable<FileReference> {
    return this.sendRequest<FileReference>('/api/file/' + fileReference.id + '/delete', RequestMethod.GET, fileReference);
  }

  getPrivacyOptions(): Observable<PrivacyOption[]> {
    return this.sendRequest<PrivacyOption[]>('/api/privacy/options', RequestMethod.GET);
  }

  downloadRequest(fileReference?: FileReference, fileRefId?: string): Observable<HttpResponse<Blob>> {
    // build endpoint url
    const id = fileReference ? fileReference.id : fileRefId;
    const endPoint = this.url + '/api/file/download/' + id;
    const token = this.authToken;
    const downloadHeaders = new HttpHeaders({
      Accept: '*/*',
      'Content-Type': 'application/json',
      Authorization: token ? token : '',
    });

    return this.http.post<Blob>(endPoint, fileReference, {
      responseType: 'blob' as 'json',
      headers: downloadHeaders,
      observe: 'response'
    });
  }


  resetUserPassword(userID: string): Observable<any> {
    return this.sendRequest<any>('/api/user/password/reset/' + userID, RequestMethod.POST);
  }


  getAniaJobCategories(): Observable<RetirementJobCategory[]> {
    return this.sendRequest<RetirementJobCategory[]>('/api/lifeprojects/retirement/categories', RequestMethod.GET);
  }

  getAniaResults(project: BoardProject): Observable<AniaRetirementResponse> {
    return this.sendRequest<AniaRetirementResponse>('/api/lifeprojects/retirement/results', RequestMethod.POST, project);
  }


  getCollaborators(): Observable<Collaborator[]> {
    return this.sendRequest<Collaborator[]>('/api/user/collaborators', RequestMethod.GET);
  }


  private buildRequestHeader(): HttpHeaders {
    const token = this.authToken;

    return new HttpHeaders(
      {
        'Content-Type': 'application/json',
        Authorization: token ? token : '',
        'Access-Control-Allow-Origin': '*'
      },
    );
  }

  private buildUploadHeader(): HttpHeaders {
    const token = this.authToken;
    return new HttpHeaders(
      {
        'Access-Control-Allow-Origin': '*',
        Authorization: token ? token : '',
      },
    );
  }


}
